import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $deleteTableColumnAtSelection,
  $getNodeTriplet,
  $getTableColumnIndexFromTableCellNode,
  $isTableCellNode,
  $isTableSelection,
} from "@lexical/table";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { $getSelection, $isRangeSelection } from "lexical";
import type { FC } from "react";
import { useCallback } from "react";
import { ToolbarIconButton } from "../../toolbar/toolbar-icon-button";

export const DeleteColumnButton: FC<{ onClick: () => void }> = ({ onClick }) => {
  const [editor] = useLexicalComposerContext();

  const handleDeleteColumn = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) && !$isTableSelection(selection)) {
        onClick();
        return;
      }

      const anchorNode = selection.anchor.getNode();
      const [cell, , table] = $getNodeTriplet(anchorNode);
      if (!$isTableCellNode(cell)) {
        onClick();
        return;
      }

      const widths = table.getColWidths();
      if (!widths) {
        $deleteTableColumnAtSelection();
        onClick();
        return;
      }

      const columnIndex = $getTableColumnIndexFromTableCellNode(cell);
      const widthOfDeletedColumn = widths[columnIndex];
      if (widthOfDeletedColumn === undefined) {
        $deleteTableColumnAtSelection();
        onClick();
        return;
      }

      $deleteTableColumnAtSelection();

      // Proportionally redistribute the deleted column's width
      const newWidths = table.getColWidths() ?? [];
      const sumOfRemainingWidths = newWidths.reduce((a, b) => a + b, 0);
      const redistributedWidths = newWidths.map(
        (w) => w + (w / sumOfRemainingWidths) * widthOfDeletedColumn,
      );

      table.setColWidths(redistributedWidths);
      onClick();
    });
  }, [editor, onClick]);

  return (
    <ToolbarIconButton
      data-analytics-id="lexical-table-menu-delete-column"
      disabled={!editor._editable}
      label="Delete column"
      icon={<DeleteOutline color="error" data-testid="DeleteIcon" fontSize="small" />}
      data-testid="table-menu-delete-column"
      aria-label="delete-column"
      onClick={handleDeleteColumn}
    />
  );
};
